/**
 * Facebook Pixel Tracking Utilities
 * Pixel ID: 1195019342732925
 * 
 * Dual tracking: Client-side (Pixel) + Server-side (Conversions API)
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Generate a unique event ID for deduplication between client and server events
 */
export function generateEventId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get Facebook click ID from URL or cookie
 */
function getFacebookClickIds(): { fbc?: string; fbp?: string } {
  if (typeof window === 'undefined') return {};
  
  const result: { fbc?: string; fbp?: string } = {};
  
  // Get fbc from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid');
  if (fbclid) {
    result.fbc = `fb.1.${Date.now()}.${fbclid}`;
  }
  
  // Try to get from cookies
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === '_fbc' && value) {
      result.fbc = value;
    }
    if (name === '_fbp' && value) {
      result.fbp = value;
    }
  }
  
  return result;
}

/**
 * Track Facebook Pixel event (client-side) + Conversions API (server-side)
 */
export async function trackFacebookEvent(
  event: string,
  params?: Record<string, any>,
  options?: {
    event_id?: string;
    user?: {
      email?: string;
      phone?: string;
      external_id?: string;
    };
  }
) {
  const eventId = options?.event_id || generateEventId(event.toLowerCase());

  // Track client-side with Facebook Pixel
  if (typeof window !== 'undefined' && (window as any).fbq) {
    try {
      (window as any).fbq('track', event, params, { eventID: eventId });
      console.log('📘 Facebook Pixel tracked:', event, eventId);
    } catch (error) {
      console.error('Facebook Pixel tracking error:', error);
    }
  } else {
    console.warn('Facebook Pixel not loaded');
  }

  // Track server-side via Conversions API (deferred to avoid blocking render)
  setTimeout(async () => {
    try {
      const { fbc, fbp } = getFacebookClickIds();
      
      const { error } = await supabase.functions.invoke('facebook-track-event', {
        body: {
          event,
          event_id: eventId,
          user: {
            ...options?.user,
            fbc,
            fbp,
          },
          properties: params,
          event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
        },
      });

      if (error) {
        console.error('Facebook CAPI tracking error:', error);
      } else {
        console.log('📘 Facebook CAPI tracked:', event, eventId);
      }
    } catch (error) {
      console.error('Facebook CAPI tracking exception:', error);
    }
  }, 0);

  return eventId;
}

/**
 * Track Lead - quando usuário gera logo/mascote
 */
export function trackFacebookLead(contentName?: string, userEmail?: string, userId?: string) {
  return trackFacebookEvent(
    'Lead',
    {
      content_name: contentName || 'Logo/Brand Generation',
      content_category: 'Brand Kit',
    },
    {
      user: {
        email: userEmail,
        external_id: userId,
      },
    }
  );
}

/**
 * Track InitiateCheckout - quando abre modal de pagamento
 */
export function trackFacebookInitiateCheckout(params: {
  value: number;
  currency?: string;
  content_name: string;
  content_ids?: string[];
  userEmail?: string;
  userId?: string;
}) {
  return trackFacebookEvent(
    'InitiateCheckout',
    {
      value: params.value,
      currency: params.currency || 'BRL',
      content_name: params.content_name,
      content_ids: params.content_ids || ['kit-marca-completo'],
      content_type: 'product',
    },
    {
      user: {
        email: params.userEmail,
        external_id: params.userId,
      },
    }
  );
}

/**
 * Track Purchase - após pagamento aprovado
 */
export function trackFacebookPurchase(params: {
  value: number;
  currency?: string;
  content_name: string;
  content_ids?: string[];
  userEmail?: string;
  userId?: string;
  orderId?: string;
}) {
  return trackFacebookEvent(
    'Purchase',
    {
      value: params.value,
      currency: params.currency || 'BRL',
      content_name: params.content_name,
      content_ids: params.content_ids || ['kit-marca-completo'],
      content_type: 'product',
    },
    {
      event_id: params.orderId ? `order_${params.orderId}` : undefined,
      user: {
        email: params.userEmail,
        external_id: params.userId,
      },
    }
  );
}
